"use client";

import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  TextInput,
  ActionIcon,
  Popover,
  ScrollArea,
  Box,
  Text,
  Group,
  Image,
  Loader,
  Portal,
  Box as MantineBox,
  Text as MantineText,
} from "@mantine/core";
import ImageUploadZone from "@/components/ImageUploadZone";
import { ImageCropOverlay } from "@/components/ImageCropOverlay";
import type { PixelCrop } from "react-image-crop";
import { IconX, IconArrowRight, IconPhotoSearch } from "@tabler/icons-react";
import { useUser } from "@clerk/nextjs";
import { useGlobalData } from "@/context/globalContext";
import { useFiltersStore } from "@/stores/filtersStore";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useUiStore } from "@/stores/uiStore";
import useSavedFilters from "@/hooks/useSavedFilters";
import { useFilterSearch, FILTER_TYPE_TO_DROPDOWN, type Suggestion } from "@/hooks/useFilterSearch";
import { notifications } from "@mantine/notifications";
import { sendGtagEvent } from "@/lib/client/gtag";
import Typewriter from "typewriter-effect";
import capitalize from "lodash/capitalize";
import styles from "./styles.module.scss";

type Props = {
  callback?: () => void;
};

const FilterAutocomplete = ({ callback }: Props) => {
  const { user } = useUser();
  const { updateSavedFilter } = useSavedFilters();
  const { vendors, brands } = useGlobalData();

  const searchText = useFiltersStore((state) => state.searchText);
  const savedFilters = useFiltersStore((state) => state.savedFilters);
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const { updateKeywordSearch: updateKeywordSearchStore, updateActiveFilter } = useFiltersStore(
    (state) => state.actions
  );
  const sourceItem = useImageSearchStore((state) => state.sourceItem);
  const cropSourceUrl = useImageSearchStore((state) => state.cropSourceUrl);
  const lastCropArea = useImageSearchStore((state) => state.lastCropArea);
  const {
    startSearch,
    exit: exitImageSearch,
    saveCropSource,
  } = useImageSearchStore((state) => state.actions);
  const { setFilterPaneOpen } = useUiStore((state) => state.actions);

  const [query, setQuery] = useState(searchText);
  const [opened, setOpened] = useState(false);
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightArrow, setHighlightArrow] = useState(false);
  const justSubmittedRef = useRef(false);
  const toastShownForRef = useRef<string | null>(null);

  const [cropImage, setCropImage] = useState<string | null>(null);

  // Image upload state
  const [imageMode, setImageMode] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);

  // Overlay state
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayPosition, setOverlayPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const filterPaneOpen = useUiStore((state) => state.filterPaneOpen);
  useEffect(() => {
    if (!filterPaneOpen) setShowOverlay(false);
  }, [filterPaneOpen]);

  // Sync local image mode with store's sourceItem
  useEffect(() => {
    if (sourceItem?.imageurl) {
      setImageMode(true);
      setPreviewUrl(sourceItem.imageurl);
    } else if (!sourceItem && imageMode) {
      setImageMode(false);
      setPreviewUrl("");
      setImageLoading(false);
    }
  }, [sourceItem, imageMode]);

  // Use shared hooks
  const { suggestions } = useFilterSearch({
    brands,
    vendors,
    query,
    activeFilters,
    maxPerType: 3,
  });
  // Generate random combinations for typewriter
  const typewriterStrings = useMemo(() => {
    if (!brands?.length) return [];

    // Common fashion colors
    const colors = [
      "black",
      "white",
      "blue",
      "red",
      "pink",
      "green",
      "yellow",
      "grey",
      "brown",
      "beige",
      "navy",
      "purple",
      "orange",
      "cream",
    ];

    // Get country names from countrySelection
    const countries = [
      "australia",
      "france",
      "italy",
      "japan",
      "usa",
      "uk",
      "germany",
      "spain",
      "canada",
      "korea",
      "china",
      "india",
      "brazil",
    ];

    // Get brand names (lowercase)
    const brandNames = brands.map((b) => b.label.toLowerCase()).slice(0, 50);

    // Helper to capitalize first letter of each word
    const capitalizeWords = (str: string) =>
      str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    // Generate 15 random combinations
    const combinations: string[] = [];
    for (let i = 0; i < 15; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const country = countries[Math.floor(Math.random() * countries.length)];
      const brand = brandNames[Math.floor(Math.random() * brandNames.length)];
      const formatted = `${capitalizeWords(color)}, ${capitalizeWords(country)}, ${capitalizeWords(brand)}`;
      // Truncate to 18 characters
      combinations.push(formatted.length > 18 ? formatted.substring(0, 18) : formatted);
    }

    return combinations;
  }, [brands]);

  const showTypewriter = !query && !isFocused;

  // Sync local query with store searchText
  useEffect(() => {
    setQuery(searchText);
  }, [searchText]);

  // Open/close dropdown based on query and suggestions
  useEffect(() => {
    if (query.length >= 2 && suggestions.length > 0) {
      setOpened(true);
      setHighlighted(null);
    } else {
      setOpened(false);
      setHighlighted(null);
    }
  }, [query, suggestions.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlighted == null || !scrollRef.current) return;
    const el = scrollRef.current.querySelectorAll("li")[highlighted];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlighted]);

  // Handle paste for image upload when in image mode
  useEffect(() => {
    if (!imageMode) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [imageMode]);

  const handleSelect = useCallback(
    (s: Suggestion) => {
      const dropdownName = FILTER_TYPE_TO_DROPDOWN[s.key];
      const currentFilters = activeFilters[dropdownName] || [];

      // Add the filter to the store
      const newFilter: Filter = {
        ...s.option,
        included: true,
        state: "include",
      };

      updateActiveFilter(
        dropdownName,
        [...currentFilters, newFilter],
        user as User | undefined,
        savedFilters || [],
        updateSavedFilter
      );

      sendGtagEvent("search", "filter_menu", "autocomplete_select", {
        filter_type: s.key,
        filter_value: s.option.label,
      });

      // Clear input and close dropdown
      setQuery("");
      setOpened(false);
      setHighlighted(null);
      callback?.();
    },
    [activeFilters, updateActiveFilter, user, savedFilters, updateSavedFilter, callback]
  );

  const handleKeywordSearch = useCallback(() => {
    const q = query.trim();
    if (!q) return;

    justSubmittedRef.current = true;
    toastShownForRef.current = null;
    callback?.();
    updateKeywordSearchStore(q, user as User | undefined, savedFilters || [], updateSavedFilter);
    sendGtagEvent("search", "search", "keyword_search", {
      keyword: q,
    });
  }, [query, callback, updateKeywordSearchStore, user, savedFilters, updateSavedFilter]);

  const handleClear = useCallback(() => {
    setQuery("");
    setOpened(false);
    updateKeywordSearchStore("", user as User | undefined, savedFilters || [], updateSavedFilter);
  }, [updateKeywordSearchStore, user, savedFilters, updateSavedFilter]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!opened) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setOpened(true);
        setHighlighted(0);
        e.preventDefault();
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleKeywordSearch();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((idx) => (idx == null ? 0 : Math.min(idx + 1, suggestions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((idx) => (idx == null ? 0 : Math.max(idx - 1, 0)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlighted !== null && suggestions[highlighted]) {
        handleSelect(suggestions[highlighted]);
      } else {
        handleKeywordSearch();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpened(false);
      setHighlighted(null);
    }
  };

  // Image upload handlers
  const handleFileChange = useCallback(
    (file: File | null) => {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) return; // 10MB limit
      if (!file.type.startsWith("image/")) return;

      setImageMode(true);

      // Close filter pane on small devices so results are visible
      if (window.innerWidth < 1251) {
        setFilterPaneOpen(false);
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [setFilterPaneOpen]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const file = e.dataTransfer.files?.[0];
      if (file?.type.startsWith("image/")) {
        handleFileChange(file);
      }
    },
    [handleFileChange]
  );

  const handleImageSearch = useCallback(async () => {
    if (!previewUrl) return;
    setImageLoading(true);

    try {
      // Create a fake item with the uploaded image URL
      const fakeItem = { imageurl: previewUrl } as SearchResultItem;
      await startSearch(fakeItem, activeFilters);
      callback?.();
      // Keep the preview visible after search (do not auto-close)
    } finally {
      setImageLoading(false);
    }
  }, [previewUrl, startSearch, activeFilters, callback]);

  // Automatically trigger image search when a preview is available
  useEffect(() => {
    if (!previewUrl) return;
    // start search immediately when an image preview is set
    handleImageSearch();
  }, [previewUrl, handleImageSearch]);

  const handleCropConfirm = useCallback(
    (croppedBase64: string, croppedPixels: PixelCrop) => {
      const src = cropImage;
      if (src) saveCropSource(src, croppedPixels);
      const fakeItem = { imageurl: croppedBase64 } as SearchResultItem;
      startSearch(fakeItem, activeFilters);
      setCropImage(null);
    },
    [cropImage, saveCropSource, startSearch, activeFilters]
  );

  const handleCropCancel = useCallback(() => {
    setCropImage(null);
  }, []);

  const handleImageModeClose = useCallback(() => {
    // Exit image search mode (just resets state, doesn't trigger search)
    exitImageSearch();

    setImageMode(false);
    setCropImage(null);
    setIsClosing(false);
    setPreviewUrl("");
    setImageLoading(false);
    setIsDragging(false);
    dragCounter.current = 0;
  }, [exitImageSearch]);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      setImageMode(false);
      setIsClosing(false);
      setIsDragging(false);
      dragCounter.current = 0;
    }
  }, [isClosing]);

  // Hidden file input for image upload
  const fileInput = (
    <MantineBox
      component="input"
      ref={fileInputRef}
      type="file"
      accept="image/*"
      style={{ display: "none" }}
      onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
    />
  );

  if (cropImage) {
    return (
      <>
        <ImageCropOverlay
          imageUrl={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          initialCropArea={
            cropImage === cropSourceUrl
              ? ((lastCropArea as PixelCrop | null) ?? undefined)
              : undefined
          }
        />
      </>
    );
  }

  if (imageMode) {
    return (
      <MantineBox className={styles.searchInput}>
        <MantineText span inherit className={styles.label}>
          IMAGE SEARCH
        </MantineText>
        {fileInput}
        <Box
          className={`${styles.imageDropZone} ${isDragging ? styles.dragging : ""} ${isClosing ? styles.closing : ""}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onAnimationEnd={handleAnimationEnd}
        >
          {previewUrl ? (
            <Group gap="sm" align="center" justify="space-between" w="100%">
              <Group gap="sm" align="center">
                <Image
                  src={previewUrl}
                  alt="Preview"
                  w={50}
                  h={50}
                  fit="cover"
                  radius="sm"
                  style={{ cursor: "pointer" }}
                  onClick={() => setCropImage(cropSourceUrl || previewUrl)}
                  title="Click to adjust crop"
                />
              </Group>
              <Group gap="xs" align="center">
                {imageLoading && <Loader size="xs" />}
                <ActionIcon
                  variant="transparent"
                  onClick={handleImageModeClose}
                  aria-label="Close image search"
                  data-testid="image-mode-close-x"
                >
                  <IconX size={18} color="black" />
                </ActionIcon>
              </Group>
            </Group>
          ) : (
            <Group
              gap="sm"
              align="center"
              justify="space-between"
              w="100%"
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: "pointer" }}
            >
              <Text size="sm" c="dimmed">
                Search by image
              </Text>
              <ActionIcon
                variant="transparent"
                mr={-3}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageModeClose();
                }}
                aria-label="Close image search"
                data-testid="image-mode-close-x"
              >
                <IconX size={18} color="black" />
              </ActionIcon>
            </Group>
          )}
        </Box>
      </MantineBox>
    );
  }

  return (
    <MantineBox className={styles.searchInput}>
      <MantineBox component="label" htmlFor="filter-autocomplete" className={styles.label}>
        SEARCH
      </MantineBox>
      {fileInput}

      <Popover
        opened={opened}
        position="bottom"
        width="target"
        withinPortal
        offset={4}
        onChange={setOpened}
        trapFocus={false}
      >
        <Popover.Target>
          <Box ref={searchBarRef} style={{ position: "relative" }}>
            {showTypewriter && (
              <Box className={styles.typewriterOverlay}>
                <Typewriter
                  options={{
                    strings: typewriterStrings,
                    autoStart: true,
                    loop: true,
                    deleteSpeed: 50,
                    delay: 80,
                  }}
                />
              </Box>
            )}
            <TextInput
              ref={inputRef}
              autoComplete="off"
              id="filter-autocomplete"
              aria-label="Search filters or keywords"
              value={query}
              onChange={(e) => {
                setQuery(e.currentTarget.value);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsFocused(true);
                if (query.length >= 2 && suggestions.length > 0) setOpened(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                setTimeout(() => {
                  setOpened(false);
                  if (justSubmittedRef.current) {
                    justSubmittedRef.current = false;
                    return;
                  }
                  const trimmed = query.trim();
                  if (trimmed && trimmed !== searchText && toastShownForRef.current !== trimmed) {
                    toastShownForRef.current = trimmed;
                    notifications.show({
                      title: `"${trimmed}" not added`,
                      message: "Press → to search this keyword",
                      color: "dark",
                      autoClose: 1000,
                      withCloseButton: false,
                    });
                    setHighlightArrow(true);
                    setTimeout(() => setHighlightArrow(false), 1000);
                  }
                }, 150);
              }}
              classNames={{
                input: styles.input,
              }}
              rightSection={
                searchText && query && query.trim() === searchText ? (
                  <ActionIcon
                    variant="filled"
                    onClick={handleClear}
                    aria-label="Clear search"
                    className={styles.clearButton}
                    data-testid="icon-x"
                  >
                    <IconX color="white" width={12} height={12} />
                  </ActionIcon>
                ) : query ? (
                  <ActionIcon
                    variant="transparent"
                    onClick={handleKeywordSearch}
                    aria-label="Submit search"
                    className={`${styles.goButton}${highlightArrow ? ` ${styles.goButtonHighlight}` : ""}`}
                    data-testid="icon-arrow"
                  >
                    <IconArrowRight color="black" size={24} />
                  </ActionIcon>
                ) : (
                  <ActionIcon
                    variant="transparent"
                    onClick={() => {
                      const rect = searchBarRef.current?.getBoundingClientRect();
                      if (rect)
                        setOverlayPosition({ top: rect.top, left: rect.left, width: rect.width });
                      setShowOverlay((prev) => !prev);
                    }}
                    aria-label="Search by image"
                    className={styles.imageSearchButton}
                    data-testid="icon-photo"
                  >
                    <IconPhotoSearch size={20} color="black" />
                  </ActionIcon>
                )
              }
            />
          </Box>
        </Popover.Target>

        <Popover.Dropdown p="xs" className={styles.dropdown}>
          <ScrollArea.Autosize mah={200} viewportRef={scrollRef}>
            <Box component="ul" m={0} p={0} style={{ listStyle: "none" }}>
              {suggestions.map((s, i) => (
                <Box
                  key={`${s.key}-${s.option.value}`}
                  component="li"
                  onMouseEnter={() => setHighlighted(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(s);
                  }}
                  className={styles.suggestionItem}
                  data-highlighted={highlighted === i}
                >
                  <Group gap={4} align="center">
                    <Text c="dimmed" className={styles.suggestionType}>
                      {capitalize(s.key)}
                    </Text>
                    <Text fw={500} className={styles.suggestionLabel}>
                      {s.option.label}
                    </Text>
                  </Group>
                </Box>
              ))}
            </Box>
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
      {showOverlay && overlayPosition && (
        <Portal>
          <MantineBox
            className={styles.imageSearchOverlay}
            style={{
              top: overlayPosition.top,
              left: overlayPosition.left,
              width: overlayPosition.width,
            }}
          >
            <ImageUploadZone
              onSearchComplete={() => setShowOverlay(false)}
              onClose={() => setShowOverlay(false)}
              label=""
            />
          </MantineBox>
        </Portal>
      )}
    </MantineBox>
  );
};

export default FilterAutocomplete;
